import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isHQ, ownBranches } from "@/lib/branchScope";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Percent, CreditCard, Pill, ClipboardList, FileSignature, Printer } from "lucide-react";
import { ImageUpload } from "@/components/crm/ImageUpload";

/* The clinic's own catalogs — taxes, payment modes and the drug list.
 *
 * These exist so nobody retypes a tax rate or a drug's standing instructions,
 * which is what lets a clinic chart inside a ten-minute appointment. Practo Ray
 * keeps the same three under Settings, and the clinic's live payment modes
 * (gpay, Via_practo_com) are exactly the sort of thing no hardcoded list would
 * have covered.
 *
 * Deleting deactivates rather than destroys: historical invoices and
 * prescriptions still reference these by name. */

const TABS = [
  { value: "taxes", label: "Tax Catalog", icon: Percent },
  { value: "payment_modes", label: "Payment Modes", icon: CreditCard },
  { value: "drugs", label: "Drug Catalog", icon: Pill },
  { value: "charting", label: "Charting Templates", icon: ClipboardList },
  { value: "consent", label: "Consent Forms", icon: FileSignature },
  { value: "printouts", label: "Printouts & Calendar", icon: Printer },
];

// The first three share one shape and use the generic table below. The last
// three each carry lists or long text, so they get their own editors.
const SIMPLE_TABS = new Set(["taxes", "payment_modes", "drugs"]);

const ENDPOINT = { taxes: "/taxes", payment_modes: "/payment-modes", drugs: "/drugs" };

const PAYMENT_KINDS = ["cash", "card", "upi", "netbanking", "cheque", "wallet", "other"];

// `table: false` keeps a field out of the list view — the drug catalogue has
// more fields than fit as columns. `suffix` is what gets appended in the table;
// without it every number rendered as a percentage, which is fine for a tax
// rate and nonsense for a pack size.
const FIELDS = {
  taxes: [
    { key: "name", label: "Name", placeholder: "GST 5%" },
    { key: "percent", label: "Rate %", type: "number", suffix: "%" },
  ],
  payment_modes: [
    { key: "name", label: "Name", placeholder: "gpay" },
    { key: "kind", label: "Type", options: PAYMENT_KINDS },
    { key: "vendor_fee", label: "Vendor fee %", type: "number", suffix: "%" },
  ],
  drugs: [
    { key: "name", label: "Drug name", placeholder: "Tretinoin 0.025% Cream" },
    { key: "form", label: "Form", placeholder: "CREAM / TABLET / SYRUP" },
    { key: "strength", label: "Strength", placeholder: "0.025%" },
    // Inventory-facing. Nothing decrements stock yet, but a catalogue with no
    // unit cannot be counted, and adding one across a live drug list later is
    // far worse than asking for it as each drug is entered.
    { key: "unit", label: "Unit", placeholder: "tablet / tube / ml" },
    { key: "stocked", label: "In-house", type: "boolean",
      hint: "Dispensed from our own shelf. Only these will ever move stock — most scripts are filled at an outside pharmacy and must not touch our counts." },
    { key: "pack_size", label: "Units per pack", type: "number", nullable: true,
      placeholder: "10", table: false },
    { key: "sku", label: "Stock code", placeholder: "optional", table: false },
    { key: "instructions", label: "Default instructions", wide: true, table: false,
      placeholder: "Apply at night on dry skin" },
  ],
};

const columnsOf = (tab) => (FIELDS[tab] || []).filter((f) => f.table !== false);

export default function ClinicSettings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("taxes");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // row being edited, or {} for new
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    if (!SIMPLE_TABS.has(tab)) { setLoading(false); return; }
    setLoading(true);
    const params = { active_only: false };
    if (tab === "drugs" && search.trim()) params.search = search.trim();
    api.get(ENDPOINT[tab], { params })
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [tab, search]);
  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    try {
      await api.delete(`${ENDPOINT[tab]}/${row.id}`);
      toast.success(`${row.name} deactivated`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reactivate = async (row) => {
    try {
      await api.put(`${ENDPOINT[tab]}/${row.id}`, { active: true });
      toast.success(`${row.name} reactivated`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setSearch(""); }}>
          <TabsList>
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`settings-tab-${t.value}`}>
                <t.icon className="mr-1.5 h-3.5 w-3.5" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          {tab === "drugs" && (
            <Input className="h-9 w-56 bg-white" placeholder="Search drugs…"
                   value={search} onChange={(e) => setSearch(e.target.value)} />
          )}
          {SIMPLE_TABS.has(tab) && (
            <Button onClick={() => setEditing({})} className="bg-navy text-white hover:bg-navy-hover"
                    data-testid="catalog-add-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Add
            </Button>
          )}
        </div>
      </div>

      {/* Charting and consent templates carry no branch either — same note. */}
      {(SIMPLE_TABS.has(tab) || tab === "charting" || tab === "consent") && !isHQ(user) && (
        <p className="text-xs text-muted-foreground" data-testid="catalog-shared-note">Changes here apply to all branches.</p>
      )}

      {tab === "charting" && <ChartingTemplatesTab />}
      {tab === "consent" && <ConsentFormsTab />}
      {tab === "printouts" && <PrintoutSettingsTab />}

      {SIMPLE_TABS.has(tab) && (
      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-cream/60 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              {columnsOf(tab).map((f) => (
                <th key={f.key} className="px-4 py-2.5 text-left font-semibold">{f.label}</th>
              ))}
              <th className="px-4 py-2.5 text-left font-semibold">Status</th>
              <th className="w-20" />
            </tr>
          </thead>
          <tbody className="divide-y divide-navy/5">
            {loading ? (
              <tr><td colSpan={columnsOf(tab).length + 2} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={columnsOf(tab).length + 2} className="px-4 py-10 text-center text-muted-foreground">Nothing here yet.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} className={row.active === false ? "opacity-50" : ""} data-testid="catalog-row">
                {columnsOf(tab).map((f) => (
                  <td key={f.key} className="px-4 py-2.5 text-ink">
                    {f.type === "boolean" ? (
                      row[f.key]
                        ? <span className="rounded-full bg-navy/10 px-2 py-0.5 text-[10px] font-semibold text-navy">In-house</span>
                        : <span className="text-muted-foreground">—</span>
                    ) : f.type === "number" && row[f.key] != null
                      ? `${row[f.key]}${f.suffix || ""}`
                      : (row[f.key] || <span className="text-muted-foreground">—</span>)}
                  </td>
                ))}
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    row.active === false ? "bg-gray-200 text-gray-600" : "bg-green-100 text-green-800"}`}>
                    {row.active === false ? "Inactive" : "Active"}
                  </span>
                </td>
                <td className="px-2 py-2.5">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(row)} title="Edit"
                            className="rounded p-1 text-muted-foreground hover:bg-nude hover:text-navy">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    {row.active === false ? (
                      <button onClick={() => reactivate(row)} title="Reactivate"
                              className="rounded px-1 text-xs text-navy hover:underline">Restore</button>
                    ) : (
                      <button onClick={() => remove(row)} title="Deactivate"
                              className="rounded p-1 text-muted-foreground hover:bg-red-50 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <CatalogDialog
        open={SIMPLE_TABS.has(tab) && !!editing}
        row={editing}
        tab={tab}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function CatalogDialog({ open, row, tab, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const isNew = open && !row?.id;
  // The three list-shaped tabs have their own editors and no FIELDS entry.
  // Reading FIELDS[tab] unguarded crashed the whole page the moment one of them
  // was selected — the render body runs even when `open` is false.
  const fields = FIELDS[tab];

  useEffect(() => {
    if (!open || !fields) return;
    const base = {};
    fields.forEach((f) => {
      const fallback = f.type === "boolean" ? false : (f.options ? f.options[0] : "");
      base[f.key] = row?.[f.key] ?? fallback;
    });
    setForm(base);
  }, [open, row, tab, fields]);

  const save = async () => {
    if (!(form.name || "").trim()) { toast.error("A name is required"); return; }
    setSaving(true);
    const body = { ...form };
    fields.forEach((f) => {
      if (f.type !== "number") return;
      // A blank pack size means "not known", not "zero of them". Coercing it to
      // 0 would tell a future stock count that a strip holds no tablets.
      body[f.key] = body[f.key] === "" || body[f.key] == null
        ? (f.nullable ? null : 0)
        : Number(body[f.key]) || 0;
    });
    try {
      if (isNew) await api.post(ENDPOINT[tab], body);
      else await api.put(`${ENDPOINT[tab]}/${row.id}`, body);
      toast.success(isNew ? "Added" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const label = TABS.find((t) => t.value === tab)?.label || "";
  if (!fields) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">
            {isNew ? `Add to ${label}` : `Edit ${row?.name || ""}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {fields.map((f) => (
            f.type === "boolean" ? (
              <label key={f.key} className="flex cursor-pointer items-start gap-2 rounded-lg bg-cream/50 p-2.5">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#1B3A5C]"
                       checked={!!form[f.key]}
                       onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.checked }))} />
                <span>
                  <span className="block text-sm font-medium text-ink">{f.label}</span>
                  {f.hint && <span className="block text-xs text-muted-foreground">{f.hint}</span>}
                </span>
              </label>
            ) : (
            <div key={f.key}>
              <label className="text-xs font-medium text-muted-foreground">{f.label}</label>
              {f.options ? (
                <Select value={form[f.key] || f.options[0]}
                        onValueChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))}>
                  <SelectTrigger className="mt-0.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => (
                      <SelectItem key={o} value={o} className="capitalize">{o.replace("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input className="mt-0.5" type={f.type || "text"} placeholder={f.placeholder}
                       value={form[f.key] ?? ""}
                       onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} />
              )}
            </div>
            )
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-navy text-white hover:bg-navy-hover"
                  data-testid="catalog-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────── Charting templates ──────────────────── */

// The bundle a doctor applies in one click. Lists are edited as comma-separated
// text: a chip editor is nicer, but this is an admin screen used a handful of
// times a year, and comma-separated is instantly obvious.
function ChartingTemplatesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/charting-templates", { params: { active_only: false } })
      .then((r) => setRows(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    const body = {
      name: form.name,
      diagnoses: splitList(form.diagnoses),
      lab_tests: splitList(form.lab_tests),
      advice: form.advice,
      follow_up_days: form.follow_up_days === "" ? null : Number(form.follow_up_days),
      prescription_items: splitList(form.drugs).map((d) => ({ drug_name: d })),
    };
    try {
      if (form.id) await api.put(`/charting-templates/${form.id}`, body);
      else await api.post("/charting-templates", body);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setEditing({})} className="bg-navy text-white hover:bg-navy-hover"
                data-testid="add-charting-template">
          <Plus className="mr-1.5 h-4 w-4" /> Add template
        </Button>
      </div>
      {rows.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          No templates yet. A template is the diagnoses, tests and review interval you
          record together every time — applying one fills a consultation in a click.
        </p>
      )}
      {rows.map((t) => (
        <div key={t.id} className={`rounded-xl border border-navy/10 bg-white p-4 shadow-sm ${t.active === false ? "opacity-50" : ""}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-ink">{t.name}</p>
              <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                {t.diagnoses?.length > 0 && <p>Diagnoses: {t.diagnoses.join(", ")}</p>}
                {t.lab_tests?.length > 0 && <p>Lab orders: {t.lab_tests.join(", ")}</p>}
                {t.prescription_items?.length > 0 && (
                  <p>Drugs: {t.prescription_items.map((d) => d.drug_name).join(", ")}</p>
                )}
                {t.follow_up_days != null && <p>Follow up after {t.follow_up_days} days</p>}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setEditing({
              ...t, diagnoses: (t.diagnoses || []).join(", "),
              lab_tests: (t.lab_tests || []).join(", "),
              drugs: (t.prescription_items || []).map((d) => d.drug_name).join(", "),
              follow_up_days: t.follow_up_days ?? "",
            })}>
              <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
            </Button>
          </div>
        </div>
      ))}
      <ListEditorDialog
        open={!!editing} value={editing} onClose={() => setEditing(null)} onSave={save}
        title={editing?.id ? "Edit template" : "New charting template"}
        fields={[
          { key: "name", label: "Template name", placeholder: "PCOS workup" },
          { key: "diagnoses", label: "Diagnoses (comma separated)" },
          { key: "lab_tests", label: "Lab tests (comma separated)" },
          { key: "drugs", label: "Drugs (comma separated)" },
          { key: "advice", label: "Advice", area: true },
          { key: "follow_up_days", label: "Follow up after (days)", type: "number" },
        ]}
      />
    </div>
  );
}

const splitList = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

/* ──────────────────── Consent forms ──────────────────── */

function ConsentFormsTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/consent-templates", { params: { active_only: false } })
      .then((r) => setRows(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    const body = { name: form.name, procedure: form.procedure, body: form.body };
    try {
      if (form.id) await api.put(`/consent-templates/${form.id}`, body);
      else await api.post("/consent-templates", body);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="max-w-2xl text-xs text-muted-foreground">
          The wording is copied onto each signed consent, so editing a form here never
          changes what a patient already agreed to.
        </p>
        <Button onClick={() => setEditing({})} className="bg-navy text-white hover:bg-navy-hover"
                data-testid="add-consent-template">
          <Plus className="mr-1.5 h-4 w-4" /> Add form
        </Button>
      </div>
      {rows.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          No consent forms yet.
        </p>
      )}
      {rows.map((t) => (
        <div key={t.id} className={`rounded-xl border border-navy/10 bg-white p-4 shadow-sm ${t.active === false ? "opacity-50" : ""}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-ink">
                {t.name}
                {t.procedure && (
                  <span className="ml-2 rounded-full bg-navy/5 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {t.procedure}
                  </span>
                )}
              </p>
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{t.body}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setEditing(t)}>
              <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
            </Button>
          </div>
        </div>
      ))}
      <ListEditorDialog
        open={!!editing} value={editing} onClose={() => setEditing(null)} onSave={save}
        title={editing?.id ? "Edit consent form" : "New consent form"}
        fields={[
          { key: "name", label: "Form name", placeholder: "Laser hair reduction consent" },
          { key: "procedure", label: "Procedure", placeholder: "LHR" },
          { key: "body", label: "What the patient is agreeing to", area: true, rows: 8 },
        ]}
      />
    </div>
  );
}

/* ──────────────── Printouts & calendar defaults ──────────────── */

function PrintoutSettingsTab() {
  // Non-admins may only save their own branches (PUT /clinic-settings/:id → 403
  // otherwise), so only those are offered.
  const { user } = useAuth();
  const [branches, setBranches] = useState(null);
  const [branchId, setBranchId] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => {
      const list = ownBranches(user, r.data || []);
      setBranches(list);
      if (list.length && !branchId) setBranchId(list[0].id);
    }).catch(() => setBranches([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!branchId) return;
    api.get(`/clinic-settings/${branchId}`).then((r) => setForm(r.data)).catch(() => {});
  }, [branchId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      [...TEXT_KEYS, ...NUM_KEYS, ...BOOL_KEYS].forEach((k) => {
        if (form[k] !== undefined && form[k] !== null && form[k] !== "") {
          body[k] = NUM_KEYS.includes(k) ? Number(form[k]) : form[k];
        }
      });
      await api.put(`/clinic-settings/${branchId}`, body);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (branches && !branches.length) {
    return <p className="py-10 text-center text-sm text-muted-foreground">No branch is assigned to your account.</p>;
  }
  if (!form) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4">
      {branches.length > 1 && (
        <Select value={branchId} onValueChange={setBranchId}>
          <SelectTrigger className="h-9 w-56 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
        <h3 className="font-display text-sm font-semibold text-navy">Clinic details on printouts</h3>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {[["clinic_name", "Clinic name"], ["registration_number", "Registration number"],
            ["clinic_phone", "Phone"], ["clinic_email", "Email"],
            ["gst_number", "GSTIN"], ["legal_name", "Legal entity name (printed after GSTIN)"]].map(([k, l]) => (
            <div key={k}>
              <label className="text-xs font-medium text-muted-foreground">{l}</label>
              <Input className="mt-0.5" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Address</label>
            <Textarea className="mt-0.5" rows={2} value={form.clinic_address || ""}
                      onChange={(e) => set("clinic_address", e.target.value)} />
          </div>
          {branchId && <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Logo (top-left of every printout)</label>
            <div className="mt-1">
              <ImageUpload fetchUrl={`/clinic-settings/${branchId}/logo`} uploadUrl={`/clinic-settings/${branchId}/logo`}
                           label="Logo" hint="Square PNG or JPG works best, under 5 MB." testId="clinic-logo-upload" />
            </div>
          </div>}
        </div>
      </div>

      <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
        <h3 className="font-display text-sm font-semibold text-navy">Page layout</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Printing onto pre-printed letterhead? Switch the header off, or it prints twice.
        </p>
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-5">
            {[["print_show_header", "Print the header"],
              ["print_show_logo", "Include the logo"],
              ["print_show_signature", "Print the doctor's signature"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Switch checked={form[k] !== false} onCheckedChange={(v) => set(k, v)} />
                {l}
              </label>
            ))}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Header title</label>
              <Input className="mt-0.5" value={form.print_header_title || ""}
                     onChange={(e) => set("print_header_title", e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Header right text</label>
              <Input className="mt-0.5" value={form.print_header_right || ""}
                     onChange={(e) => set("print_header_right", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Footer</label>
              <Input className="mt-0.5" value={form.print_footer_text || ""}
                     onChange={(e) => set("print_footer_text", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[["print_margin_top_mm", "Top"], ["print_margin_bottom_mm", "Bottom"],
              ["print_margin_left_mm", "Left"], ["print_margin_right_mm", "Right"]].map(([k, l]) => (
              <div key={k}>
                <label className="text-xs font-medium text-muted-foreground">{l} margin (mm)</label>
                <Input className="mt-0.5" type="number" value={form[k] ?? ""}
                       onChange={(e) => set(k, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
        <h3 className="font-display text-sm font-semibold text-navy">Calendar defaults</h3>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {[["default_appointment_minutes", "Default appointment (mins)"],
            ["day_start_hour", "Day starts (hour)"], ["day_end_hour", "Day ends (hour)"]].map(([k, l]) => (
            <div key={k}>
              <label className="text-xs font-medium text-muted-foreground">{l}</label>
              <Input className="mt-0.5" type="number" value={form[k] ?? ""}
                     onChange={(e) => set(k, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-navy text-white hover:bg-navy-hover"
                data-testid="save-printout-settings">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}

const TEXT_KEYS = ["clinic_name", "clinic_address", "clinic_phone", "clinic_email",
                   "registration_number", "gst_number", "legal_name",
                   "print_header_title", "print_header_left",
                   "print_header_right", "print_footer_text"];
const NUM_KEYS = ["print_margin_top_mm", "print_margin_bottom_mm", "print_margin_left_mm",
                  "print_margin_right_mm", "default_appointment_minutes",
                  "day_start_hour", "day_end_hour"];
const BOOL_KEYS = ["print_show_header", "print_show_logo", "print_show_signature"];

/* Shared editor for the two list-shaped catalogs above. */
function ListEditorDialog({ open, value, onClose, onSave, title, fields }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(value || {}); }, [open, value]);

  const submit = async () => {
    if (!(form.name || "").trim()) { toast.error("A name is required"); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-navy">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-medium text-muted-foreground">{f.label}</label>
              {f.area ? (
                <Textarea className="mt-0.5" rows={f.rows || 3} value={form[f.key] || ""}
                          onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} />
              ) : (
                <Input className="mt-0.5" type={f.type || "text"} placeholder={f.placeholder}
                       value={form[f.key] ?? ""}
                       onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
